<template>
  <div class="kb-rows">
    <!-- Your own profile: these switches are locked -->
    <p
      v-if="canManageAdminRoles && isCurrentUser"
      class="kb-row-note my-3"
    >
      This is you. You can't change your own admin roles, ban yourself or delete your own account.
    </p>

    <div
      v-if="canManageAdminRoles"
      class="kb-row"
    >
      <div>
        <h4 class="kb-row-title">
          Administrator
        </h4>
        <p class="kb-row-hint">
          Manages all members and SubKrewes
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingAdmin"
          label="Saving"
        />
        <ToggleSwitch
          v-model="isAdmin"
          :disabled="isUpdatingAdmin || isCurrentUser"
          aria-label="Administrator"
          @change="toggleAdminStatus"
        />
      </div>
    </div>

    <div
      v-if="canManageAdminRoles"
      class="kb-row"
    >
      <div>
        <h4 class="kb-row-title">
          Band administrator
        </h4>
        <p class="kb-row-hint">
          Manages band-specific functions and settings
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingBandAdmin"
          label="Saving"
        />
        <ToggleSwitch
          v-model="isBandAdmin"
          :disabled="isUpdatingBandAdmin || isCurrentUser"
          aria-label="Band administrator"
          @change="toggleBandAdminStatus"
        />
      </div>
    </div>

    <div
      v-if="canManageAdminRoles"
      class="kb-row"
    >
      <div>
        <h4 class="kb-row-title">
          Red shirt administrator
        </h4>
        <p class="kb-row-hint">
          Manages red shirts and specialty flows
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingRedshirtAdmin"
          label="Saving"
        />
        <ToggleSwitch
          v-model="isRedshirtAdmin"
          :disabled="isUpdatingRedshirtAdmin || isCurrentUser"
          aria-label="Red shirt administrator"
          @change="toggleRedshirtAdminStatus"
        />
      </div>
    </div>

    <div
      class="kb-row"
    >
      <div>
        <h4 class="kb-row-title">
          Check-in volunteer
        </h4>
        <p class="kb-row-hint">
          Can scan QR codes and check members in at events
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingCheckinVolunteer"
          label="Saving"
        />
        <ToggleSwitch
          v-model="isCheckinVolunteer"
          :disabled="isUpdatingCheckinVolunteer"
          aria-label="Check-in volunteer"
          @change="toggleCheckinVolunteerStatus"
        />
      </div>
    </div>

    <div
      class="kb-row"
    >
      <div>
        <h4 class="kb-row-title">
          Beta tester
        </h4>
        <p class="kb-row-hint">
          Can use the site while it is in maintenance mode
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingBetaTester"
          label="Saving"
        />
        <ToggleSwitch
          v-model="isBetaTester"
          :disabled="isUpdatingBetaTester"
          aria-label="Beta tester"
          @change="toggleBetaTesterStatus"
        />
      </div>
    </div>

    <!-- Ban status toggle; the date and reason show in the drawer summary -->
    <div
      class="kb-row"
    >
      <div>
        <h4 class="kb-row-title">
          Banned
        </h4>
        <p class="kb-row-hint">
          Can't sign in to the app
        </p>
      </div>
      <div class="kb-row-aside">
        <KbSpinner
          v-if="isUpdatingBanStatus"
          label="Saving"
        />
        <ToggleSwitch
          v-model="isBanned"
          :disabled="isUpdatingBanStatus || isCurrentUser"
          aria-label="Banned"
          @change="onBanStatusChange"
        />
      </div>
    </div>

    <!-- Ban dialog -->
    <Dialog
      :visible="showBanDialog"
      modal
      header="Ban user"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :closable="false"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          You're about to ban <strong>{{ member.name || 'this member' }}</strong>.
        </p>
        <p class="kb-dialog-warning">
          They'll be removed from their SubKrewe and can't sign in to the app.
        </p>

        <div class="kb-field">
          <label for="ban-reason">Reason</label>
          <Textarea
            id="ban-reason"
            v-model="banReason"
            size="small"
            fluid
            autofocus
            rows="3"
            :invalid="!!banReasonError"
            aria-describedby="ban-reason-error"
            placeholder="Why this member is banned"
          />
          <small
            v-if="banReasonError"
            id="ban-reason-error"
            class="kb-field-error"
          >{{ banReasonError }}</small>
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          @click="cancelBan"
        />
        <Button
          label="Ban user"
          severity="danger"
          @click="confirmBan"
        />
      </template>
    </Dialog>

    <!-- Delete user - only full admins -->
    <div
      v-if="canManageAdminRoles"
      class="kb-row"
    >
      <div>
        <h4 class="kb-row-title">
          Delete user
        </h4>
        <p class="kb-row-hint">
          Permanently deletes this user and their data
        </p>
      </div>
      <div class="kb-row-aside">
        <Button
          icon="pi pi-trash"
          label="Delete user"
          outlined
          severity="danger"
          size="small"
          :disabled="isCurrentUser || isDeletingUser"
          @click="showDeleteDialog = true"
        />
      </div>
    </div>

    <!-- Delete user dialog -->
    <Dialog
      :visible="showDeleteDialog"
      modal
      header="Delete user"
      :style="{ width: '28rem' }"
      :breakpoints="{ '575px': 'calc(100vw - 2rem)' }"
      :closable="false"
    >
      <div class="kb-form">
        <p class="kb-dialog-lead">
          You're about to delete <strong>{{ member.name || 'this member' }}</strong> and all of their data.
        </p>
        <p class="kb-dialog-warning">
          This can't be undone: their sign-in account and member record are deleted for good.
        </p>

        <div class="kb-field">
          <label for="delete-confirmation">Type <span class="px-1 rounded bg-kb-hover">DELETE</span> to confirm</label>
          <InputText
            id="delete-confirmation"
            v-model="deleteConfirmation"
            size="small"
            fluid
            autofocus
            autocomplete="off"
            :invalid="!!deleteConfirmationError"
            aria-describedby="delete-confirmation-error"
          />
          <small
            v-if="deleteConfirmationError"
            id="delete-confirmation-error"
            class="kb-field-error"
          >{{ deleteConfirmationError }}</small>
        </div>
      </div>

      <template #footer>
        <Button
          label="Cancel"
          text
          severity="secondary"
          @click="cancelDelete"
        />
        <Button
          label="Delete user"
          severity="danger"
          :loading="isDeletingUser"
          @click="confirmDelete"
        />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useCurrentUser } from 'vuefire'
import type { Member } from '~/shared/types/member'
import { memberIsAdmin, memberIsBandAdmin } from '~/shared/utils/memberUtils'

interface Props {
  member: Member
}

const props = defineProps<Props>()

const membersStore = useMembersStore()
const toast = useToast()
const currentUser = useCurrentUser()
const { closeDrawer } = useDrawer()

// Get current member to check permissions
const currentMember = await useCurrentMember()
const isCurrentUserAdmin = memberIsAdmin(currentMember)
const isCurrentUserBandAdmin = memberIsBandAdmin(currentMember)

// Computed property to check if current user should see admin-only sections
// Only full admins can manage admin roles, ban, delete users, etc.
// Redshirt admins and band admins should never see these controls
const canManageAdminRoles = computed(() => {
  return isCurrentUserAdmin && !isCurrentUserBandAdmin
})

// Admin status management
const isAdmin = ref(Boolean(props.member.roles?.admin))
const isUpdatingAdmin = ref(false)

// Band admin status management
const isBandAdmin = ref(Boolean(props.member.roles?.band_admin))
const isUpdatingBandAdmin = ref(false)

// Redshirt admin status management
const isRedshirtAdmin = ref(Boolean(props.member.roles?.redshirt_admin))
const isUpdatingRedshirtAdmin = ref(false)

// Check-in volunteer status management
const isCheckinVolunteer = ref(Boolean(props.member.roles?.checkinVolunteer))
const isUpdatingCheckinVolunteer = ref(false)

// Beta tester status management
const isBetaTester = ref(Boolean(props.member.beta_tester))
const isUpdatingBetaTester = ref(false)

// Ban status management
const isBanned = ref(Boolean(props.member.banned))
const isUpdatingBanStatus = ref(false)
const showBanDialog = ref(false)
const banReason = ref('')
const banReasonError = ref('')

// Delete user management
const showDeleteDialog = ref(false)
const deleteConfirmation = ref('')
const deleteConfirmationError = ref('')
const isDeletingUser = ref(false)

// Computed property to determine if this is the current logged-in user
const isCurrentUser = computed(() => {
  return currentUser.value?.uid === props.member.id
})

// Watch for changes in member data for admin status
watch(() => props.member.roles?.admin, (newValue) => {
  // Explicitly set to Boolean to handle undefined values properly
  isAdmin.value = Boolean(newValue)
}, { immediate: true })

// Watch for changes in member data for band admin status
watch(() => props.member.roles?.band_admin, (newValue) => {
  // Explicitly set to Boolean to handle undefined values properly
  isBandAdmin.value = Boolean(newValue)
}, { immediate: true })

// Watch for changes in member data for redshirt admin status
watch(() => props.member.roles?.redshirt_admin, (newValue) => {
  isRedshirtAdmin.value = Boolean(newValue)
}, { immediate: true })

// Watch for changes in member data for check-in volunteer status
watch(() => props.member.roles?.checkinVolunteer, (newValue) => {
  isCheckinVolunteer.value = Boolean(newValue)
}, { immediate: true })

// Watch for changes in member data for beta tester status
watch(() => props.member.beta_tester, (newValue) => {
  isBetaTester.value = Boolean(newValue)
}, { immediate: true })

// Watch for changes in member data for ban status
watch(() => props.member.banned, (newValue) => {
  // Explicitly use Boolean conversion for consistency
  isBanned.value = Boolean(newValue)
}, { immediate: true })

// Toggle admin status function
const toggleAdminStatus = async () => {
  if (isCurrentUser.value) return // Extra safety check

  isUpdatingAdmin.value = true

  try {
    const success = await membersStore.toggleAdminStatus(props.member, isAdmin.value)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Admin Status Updated',
        detail: `${props.member.name} is ${isAdmin.value ? 'now' : 'no longer'} an administrator`,
        life: 3000,
      })
    }
    else {
      // If failed, revert the UI state
      isAdmin.value = !isAdmin.value
      toast.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: 'Could not update administrator status',
        life: 3000,
      })
    }
  }
  catch (error) {
    // If error, revert the UI state
    isAdmin.value = !isAdmin.value
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingAdmin.value = false
  }
}

// Toggle band admin status function
const toggleBandAdminStatus = async () => {
  if (isCurrentUser.value) return // Extra safety check

  isUpdatingBandAdmin.value = true

  try {
    const success = await membersStore.toggleBandAdminStatus(props.member, isBandAdmin.value)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Band Admin Status Updated',
        detail: `${props.member.name} is ${isBandAdmin.value ? 'now' : 'no longer'} a band administrator`,
        life: 3000,
      })
    }
    else {
      // If failed, revert the UI state
      isBandAdmin.value = !isBandAdmin.value
      toast.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: 'Could not update band administrator status',
        life: 3000,
      })
    }
  }
  catch (error) {
    // If error, revert the UI state
    isBandAdmin.value = !isBandAdmin.value
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingBandAdmin.value = false
  }
}

// Toggle redshirt admin status function
const toggleRedshirtAdminStatus = async () => {
  if (isCurrentUser.value) return // Extra safety check

  isUpdatingRedshirtAdmin.value = true

  try {
    const success = await membersStore.toggleRedshirtAdminStatus(props.member, isRedshirtAdmin.value)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Red Shirt Admin Status Updated',
        detail: `${props.member.name} is ${isRedshirtAdmin.value ? 'now' : 'no longer'} a Red Shirt administrator`,
        life: 3000,
      })
    }
    else {
      // If failed, revert the UI state
      isRedshirtAdmin.value = !isRedshirtAdmin.value
      toast.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: 'Could not update redshirt administrator status',
        life: 3000,
      })
    }
  }
  catch (error) {
    // If error, revert the UI state
    isRedshirtAdmin.value = !isRedshirtAdmin.value
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingRedshirtAdmin.value = false
  }
}

// Toggle check-in volunteer status function
const toggleCheckinVolunteerStatus = async () => {
  isUpdatingCheckinVolunteer.value = true

  try {
    const success = await membersStore.toggleCheckinVolunteerStatus(props.member, isCheckinVolunteer.value)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Check-in Volunteer Status Updated',
        detail: `${props.member.name} is ${isCheckinVolunteer.value ? 'now' : 'no longer'} a check-in volunteer`,
        life: 3000,
      })
    }
    else {
      // If failed, revert the UI state
      isCheckinVolunteer.value = !isCheckinVolunteer.value
      toast.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: 'Could not update check-in volunteer status',
        life: 3000,
      })
    }
  }
  catch (error) {
    // If error, revert the UI state
    isCheckinVolunteer.value = !isCheckinVolunteer.value
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingCheckinVolunteer.value = false
  }
}

const toggleBetaTesterStatus = async () => {
  isUpdatingBetaTester.value = true

  try {
    const success = await membersStore.toggleBetaTesterStatus(props.member, isBetaTester.value)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'Beta Tester Status Updated',
        detail: `${props.member.name} is ${isBetaTester.value ? 'now' : 'no longer'} a beta tester`,
        life: 3000,
      })
    }
    else {
      isBetaTester.value = !isBetaTester.value
      toast.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: 'Could not update beta tester status',
        life: 3000,
      })
    }
  }
  catch (error) {
    isBetaTester.value = !isBetaTester.value
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingBetaTester.value = false
  }
}

// Ban status change handler

// Ban management functions
const onBanStatusChange = () => {
  // Safety check for self-banning
  if (isCurrentUser.value) {
    isBanned.value = false
    return
  }

  // If toggling to banned, show dialog
  if (isBanned.value) {
    showBanDialog.value = true
  }
  else {
    // If unbanning, just do it directly
    updateBanStatus(false)
  }
}

const cancelBan = () => {
  // Reset UI state
  isBanned.value = false
  showBanDialog.value = false
  banReason.value = ''
  banReasonError.value = ''
}

const confirmBan = () => {
  // Validate reason is provided
  if (!banReason.value.trim()) {
    banReasonError.value = 'A reason is required when banning a user'
    return
  }

  // Clear error if it was previously set
  banReasonError.value = ''

  // Close dialog and update ban status
  showBanDialog.value = false
  updateBanStatus(true, banReason.value)
}

const updateBanStatus = async (banned: boolean, reason?: string) => {
  isUpdatingBanStatus.value = true

  try {
    const success = await membersStore.toggleBanStatus(props.member, banned, reason)

    if (success) {
      toast.add({
        severity: banned ? 'warn' : 'success',
        summary: banned ? 'User Banned' : 'User Unbanned',
        detail: banned
          ? `${props.member.name} has been banned and removed from their SubKrewe`
          : `${props.member.name} has been unbanned`,
        life: 3000,
      })
    }
    else {
      // If failed, revert the UI state
      isBanned.value = !banned
      toast.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: `Could not ${banned ? 'ban' : 'unban'} user`,
        life: 3000,
      })
    }
  }
  catch (error) {
    // If error, revert the UI state
    isBanned.value = !banned
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isUpdatingBanStatus.value = false
    banReason.value = ''
  }
}

// Delete user management functions
const cancelDelete = () => {
  deleteConfirmation.value = ''
  deleteConfirmationError.value = ''
  showDeleteDialog.value = false
}

const confirmDelete = async () => {
  if (deleteConfirmation.value.trim() !== 'DELETE') {
    deleteConfirmationError.value = 'You must type DELETE to confirm'
    return
  }

  deleteConfirmationError.value = ''
  isDeletingUser.value = true

  try {
    const success = await membersStore.deleteUser(props.member)

    if (success) {
      toast.add({
        severity: 'success',
        summary: 'User Deleted',
        detail: `${props.member.name} has been permanently deleted`,
        life: 3000,
      })
      closeDrawer()
    }
    else {
      toast.add({
        severity: 'error',
        summary: 'Delete Failed',
        detail: 'Could not delete user',
        life: 3000,
      })
    }
  }
  catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: `${error}`,
      life: 3000,
    })
  }
  finally {
    isDeletingUser.value = false
    showDeleteDialog.value = false
    deleteConfirmation.value = ''
  }
}
</script>
